import { FormEvent, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { updatePassword } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { httpsCallable } from '../lib/callable';
import { AlertCircle, CheckCircle2, KeyRound } from 'lucide-react';
import { auth } from '../firebase';
import { CLOUD_FUNCTIONS } from '../constants/firebase';
import { useAuth } from '../contexts/AuthContext';
import { BrandLogo, useBranding } from '../contexts/BrandingContext';

export default function ChangePassword() {
  const { branding } = useBranding();
  const { user, loading, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  if (loading) return <div className="flex min-h-screen items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!mustChangePassword && !done && !saving) return <Navigate to="/" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (password.length < 10 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
      setError('Use at least 10 characters with uppercase, lowercase, and a number.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await updatePassword(user, password);
      const completeChange = httpsCallable(getFunctions(), CLOUD_FUNCTIONS.COMPLETE_INITIAL_PASSWORD_CHANGE);
      await completeChange({});
      setDone(true);
      setTimeout(() => navigate('/', { replace: true }), 1200);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code || '';
      setError(code.includes('requires-recent-login')
        ? 'Your session has expired. Sign out and use the temporary password again.'
        : 'We could not update your password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#F7F9FB] px-6 py-14 text-[#0B1C30]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", backgroundImage: 'radial-gradient(circle at 88% 8%, rgba(0,137,137,.16), transparent 30%), radial-gradient(circle at 10% 92%, rgba(255,185,92,.12), transparent 26%)' }}>
      <section className="w-full max-w-[520px] rounded-[22px] border border-[#DCE2E5] bg-white px-8 py-10 shadow-[0_18px_48px_rgba(15,42,50,0.12)] sm:px-12 sm:py-12">
        <div className="mb-10 flex items-center gap-4">
          <BrandLogo className="h-[52px] w-[52px] rounded-[13px] object-contain" />
          <div><div className="text-[21px] font-bold text-[#001820]">{branding.productName}</div><div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#5F696C]">Account Security</div></div>
        </div>

        {done ? (
          <div>
            <CheckCircle2 className="mb-5 h-10 w-10 text-[var(--brand-primary)]" />
            <h1 className="text-4xl font-normal tracking-[-0.04em] text-[#001820]">Password updated</h1>
            <p className="mt-4 text-[15px] leading-6 text-[#5F696C]">Your account is ready. Taking you to DocumentSpark…</p>
          </div>
        ) : (
          <>
            <KeyRound className="mb-5 h-9 w-9 text-[var(--brand-primary)]" />
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">First sign-in</p>
            <h1 className="text-4xl font-normal tracking-[-0.04em] text-[#001820]">Create your password</h1>
            <p className="mb-8 mt-4 text-[15px] leading-6 text-[#5F696C]">Replace the temporary password before continuing to your workspace.</p>
            {error && <div className="mb-5 flex items-center gap-2 rounded-[13px] border border-red-200 bg-red-50 p-4 text-sm text-red-700"><AlertCircle className="h-5 w-5 flex-shrink-0" />{error}</div>}
            <form onSubmit={submit} className="space-y-5">
              <label className="block text-sm font-bold">New password<input autoFocus required minLength={10} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-14 w-full rounded-[13px] border border-[#CAD2D5] px-4 outline-none focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/15" /></label>
              <label className="block text-sm font-bold">Confirm new password<input required minLength={10} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 h-14 w-full rounded-[13px] border border-[#CAD2D5] px-4 outline-none focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/15" /></label>
              <p className="text-xs leading-5 text-[#6B777A]">At least 10 characters, including uppercase, lowercase, and a number.</p>
              <button disabled={saving} className="w-full rounded-full bg-[#001820] px-5 py-3.5 text-[15px] font-bold text-white hover:bg-[#00313A] disabled:opacity-50">{saving ? 'Updating password…' : 'Set password and continue'}</button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
